/*
 * rheincool — Filter-Alias-Übersetzung (Umsetzungsplan 1.2)
 * Ratgeber-CTAs, kW-Rechner und Mega-Menü-Pills verlinken sprechende
 * Parameter (?leistung=2-5-kw&heizfunktion=ja). Dieses Modul liest die
 * zentrale Tabelle (#rc-filter-aliases, snippets/filter-alias-config.liquid),
 * übersetzt sie in Search-&-Discovery-Parameter und lädt die gefilterte
 * Ansicht via rc-facets-page. Unbekannte Parameter bleiben unberührt.
 *
 * Aufgelöst wird gegen das Filterformular, das diese Seite bereits zeigt
 * (19.09.2026). Vorher gingen Bereiche als `.gte/.lte` an Metafeld-Filter,
 * die Shopify stillschweigend ignoriert, und Boolean-Werte als "true", die
 * zwar filtern, aber nie als aktiv erscheinen. Jetzt wird nur gesendet, was
 * das Formular selbst als Checkbox anbietet — dann erscheinen Chip und
 * Haken, und ein Wert ohne Treffer kann gar nicht erst entstehen.
 *
 * Steht ein aufgelöster Filter schon vollständig in der URL (Mega-Menü-Pills
 * rendern ihre Werte serverseitig mit), wird nur die Adresszeile aufgeräumt —
 * kein zweiter Abruf. Neue Werte nach einem Import lösen dagegen einen
 * Abruf mit der ergänzten Liste aus.
 */
(() => {
  'use strict';

  const configEl = document.getElementById('rc-filter-aliases');
  const page = document.querySelector('rc-facets-page');
  if (!configEl || !page) return;

  let aliases;
  try {
    aliases = JSON.parse(configEl.textContent);
  } catch (error) {
    console.warn('[rc-filter-alias] Konfiguration unlesbar', error);
    return;
  }

  const current = new URLSearchParams(window.location.search);
  const isAlias = (key) => Object.prototype.hasOwnProperty.call(aliases, key);
  const aliasKeys = Array.from(new Set(Array.from(current.keys()))).filter(isAlias);
  if (!aliasKeys.length) return;

  const form = page.querySelector('[data-facets-form]');

  /* Angebotene Werte eines Filters. Gesperrte Checkboxen (0 Treffer, nicht
     aktiv) zählen nicht — sie würden eine leere Seite erzeugen. Werte hinter
     „Mehr anzeigen" stehen im <details> ebenfalls im DOM. */
  const inputsFor = (param) => {
    if (!form) return [];
    return Array.from(form.querySelectorAll('input[name]')).filter(
      (input) => input.name === param && input.type !== 'hidden' && !input.disabled
    );
  };

  const NUMBER = /^-?\d+(?:[.,]\d+)?$/;
  const toNumber = (text) => parseFloat(String(text).replace(',', '.'));

  /* Sichtbare Beschriftung eines Werts, ohne Trefferzahl „(88)".
     facets.liquid legt die Checkbox in ihr <label>; label[for] und
     aria-label fangen ein späteres Umbauen des Markups ab. */
  const labelText = (input) => {
    let label = input.closest('label');
    if (!label && input.id && form && window.CSS && CSS.escape) {
      label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    }
    if (!label) return input.getAttribute('aria-label') || '';
    const own = label.querySelector('.rc-facets__option-label');
    if (own) return own.textContent;
    const copy = label.cloneNode(true);
    copy.querySelectorAll('input, .rc-facets__count, [aria-hidden="true"]').forEach((node) => node.remove());
    return copy.textContent;
  };

  /* Zahlenbereich eines Filterwerts: Einzelwert ("25") oder S&D-Wertegruppe
     ("2,5 – 3,4 kW", "bis 2,4 kW", "über 5 kW", "ab 46 m²"). */
  const rangeOf = (input) => {
    const raw = input.value.trim();
    if (NUMBER.test(raw)) {
      const n = toNumber(raw);
      return { min: n, max: n, minIn: true, maxIn: true };
    }
    const text = labelText(input)
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const numbers = (text.match(/\d+(?:[.,]\d+)?/g) || []).map(toNumber);
    if (!numbers.length) return null;
    if (numbers.length >= 2) {
      return {
        min: Math.min(numbers[0], numbers[1]),
        max: Math.max(numbers[0], numbers[1]),
        minIn: true,
        maxIn: true,
      };
    }
    const n = numbers[0];
    if (/^(ab\b|mind|≥|>=)/.test(text)) return { min: n, max: Infinity, minIn: true, maxIn: false };
    if (/^(über|ueber|mehr als|>)/.test(text)) return { min: n, max: Infinity, minIn: false, maxIn: false };
    if (/^(bis\b|max|höchstens|≤|<=)/.test(text)) return { min: -Infinity, max: n, minIn: false, maxIn: true };
    if (/^(unter|weniger als|<)/.test(text)) return { min: -Infinity, max: n, minIn: false, maxIn: false };
    return { min: n, max: n, minIn: true, maxIn: true };
  };

  /* Berührt der Wertebereich den Alias-Bereich [gte, lte]? */
  const touches = (range, gte, lte) => {
    if (gte !== undefined && !(range.max > gte || (range.max === gte && range.maxIn))) return false;
    if (lte !== undefined && !(range.min < lte || (range.min === lte && range.minIn))) return false;
    return true;
  };

  /* Eine Zuordnung auflösen → { param, values } oder null */
  const resolveOne = (mapping, defaultParam) => {
    if (!mapping || typeof mapping !== 'object') return null;
    const param = mapping.param || defaultParam;
    if (!param) return null;

    if (mapping.eq !== undefined) {
      const wanted = [].concat(mapping.eq).map(String);
      // sort_by & Co. sind keine Checkboxen — unverändert übernehmen
      if (!param.startsWith('filter.')) return { param, values: wanted };
      const offered = inputsFor(param).map((input) => input.value);
      const values = wanted.filter((value) => offered.includes(value));
      return values.length ? { param, values } : null;
    }

    if (mapping.gte !== undefined || mapping.lte !== undefined) {
      const values = inputsFor(param)
        .filter((input) => {
          const range = rangeOf(input);
          return range && touches(range, mapping.gte, mapping.lte);
        })
        .map((input) => input.value);
      return values.length ? { param, values: Array.from(new Set(values)) } : null;
    }

    return null;
  };

  /* Eine Alternative samt „und"-Teilen auflösen → [{ param, values }, …]
     oder null. Alle Teile müssen auf dieser Seite etwas finden, sonst gilt
     die Alternative als nicht auflösbar und die nächste wird versucht.
     Beispiel 5-0-kw: S&D-Gruppe „3,5 – 5,0 kW" UND Raumgröße 46–55 m² —
     die Gruppe allein zeigte zu mehr als der Hälfte Geräte unter 5 kW. */
  const resolveOption = (option, defaultParam) => {
    const first = resolveOne(option, defaultParam);
    if (!first) return null;
    const hits = [first];
    const parts = option.und === undefined ? [] : [].concat(option.und);
    for (let i = 0; i < parts.length; i += 1) {
      const hit = resolveOne(parts[i], defaultParam);
      if (!hit) return null;
      hits.push(hit);
    }
    return hits;
  };

  /* Marke & Co.: Schreibweise der Checkbox dieser Seite übernehmen.
     filter.p.vendor=daikin liefert 0 Treffer, nur „Daikin" greift. */
  const slug = (text) =>
    String(text)
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const matchRendered = (param, raw) => {
    const inputs = inputsFor(param);
    const wanted = slug(raw);
    if (!wanted) return null;
    const exact = inputs.find((input) => slug(input.value) === wanted);
    if (exact) return { param, values: [exact.value] };
    // „mitsubishi" → „Mitsubishi Electric", aber nur wenn eindeutig
    const prefix = inputs.filter((input) => slug(input.value).startsWith(`${wanted}-`));
    return prefix.length === 1 ? { param, values: [prefix[0].value] } : null;
  };

  const resolved = new Map();
  const unresolved = [];

  current.forEach((value, key) => {
    if (!isAlias(key)) return;
    const alias = aliases[key];
    let hits = null;

    if (alias && alias.values === 'passthrough') {
      const hit = matchRendered(alias.param, value);
      hits = hit ? [hit] : null;
    } else if (alias && alias.values && typeof alias.values === 'object') {
      // Nur eigene Einträge: ?leistung=constructor darf nicht auf
      // Object.prototype greifen.
      const mapping = Object.prototype.hasOwnProperty.call(alias.values, value)
        ? alias.values[value]
        : undefined;
      const options = mapping === undefined ? [] : [].concat(mapping);
      for (let i = 0; i < options.length && !hits; i += 1) {
        hits = resolveOption(options[i], alias.param);
      }
    }

    if (!hits) {
      unresolved.push(`${key}=${value}`);
      return;
    }
    hits.forEach((hit) => {
      if (!resolved.has(hit.param)) resolved.set(hit.param, new Set());
      hit.values.forEach((v) => resolved.get(hit.param).add(v));
    });
  });

  if (unresolved.length) {
    console.warn('[rc-filter-alias] Kein passender Filter auf dieser Seite für', unresolved.join(', '));
  }

  /* Ziel: alle Nicht-Alias-Parameter bleiben; ein aufgelöster Parameter
     ersetzt vorhandene Werte gleichen Namens (der Alias bestimmt ihn). */
  const kept = new URLSearchParams();
  current.forEach((value, key) => {
    if (!isAlias(key)) kept.append(key, value);
  });
  const target = new URLSearchParams();
  kept.forEach((value, key) => {
    if (!resolved.has(key)) target.append(key, value);
  });
  resolved.forEach((values, param) => values.forEach((v) => target.append(param, v)));

  const signature = (params) =>
    Array.from(params)
      .map(([key, value]) => `${key}=${value}`)
      .sort()
      .join('&');

  const query = target.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ''}`;

  if (signature(target) === signature(kept)) {
    // Die Seite zeigt bereits genau diese Filter — nur die Adresszeile
    // aufräumen (Alias raus), kein zweiter Abruf.
    history.replaceState(history.state, '', `${url}${window.location.hash}`);
    return;
  }

  // Sprechende URL in der Adresszeile lassen wäre schöner, aber die
  // Section-Rendering-Antwort braucht die echten Filter-Parameter — daher
  // einmalig ersetzen; Canonical zeigt ohnehin auf die Filter-URL (SEO §13).
  if (typeof customElements !== 'undefined') {
    customElements.whenDefined('rc-facets-page').then(() => page.load(url));
  }
})();
